import { html } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import { ButtressEntity } from '@buttress/crag';

import NSJobComponent from '../../job.js';
import JobSchema from '../../../../models/job-schema.js';
import { callLambda } from '../../../../helpers/lambda.js';
import { renderDate, renderMoney } from '../../../../helpers/format-render.js';
import '../../../../ns-elements/ns-job-layout.js';
import '@awesome.me/webawesome/dist/components/button/button.js';
import definition from '@job-schemas/subscription/reactivate.json';
import { jobSchema } from '../../job-schema.js';

@customElement(NSReactivateSubscription.is)
export default class NSReactivateSubscription extends NSJobComponent {
  static get is() {
    return 'ns-reactivate-subscription';
  }

  @state()
  private __subscription?: ButtressEntity;

  // Complete is the job's own button so it leads the bar; the job schema lists cancel first
  static get schema(): JobSchema {
    return jobSchema(definition, {
      outcomes: { cancel: { type: 'button', label: 'Cancel' }, complete: { type: 'hidden' } },
    });
  }

  async connectedCallback() {
    await super.connectedCallback();

    if (!this._db) throw new Error('Unable to find db service');

    if (!this._auth) throw new Error('Unable to find auth service');

    if (!this._job) throw new Error('Unable to find job');

    if (!this._directory) throw new Error('Directory service not found');
    if (!this._directory.me || !this._directory.me.companyId)
      throw new Error('Company id not found in the directory service');

    this.__subscription = await this.loadInput('subscription', 'subscriptionId');
  }

  private async _outcomeCompleteHandler() {
    try {
      this.clearError();
      if (!this._auth) throw new Error('Missing auth service');
      if (!this.__subscription) {
        throw new Error('Unable to find a subscription to be cancelled');
      }

      this.loading('Re-activating Subscription');

      await callLambda(
        'subscription/reactivate',
        'POST',
        this._auth.token,
        JSON.stringify({
          subscriptionId: this.__subscription.id,
        }),
      );

      this.loaded();
      this.outcome('complete');
    } catch (err: any) {
      this.loaded();
      this.showError(err, 'Something went wrong, our support is working on fixing it and they will contact you');
      throw new Error(err.message);
    }
  }

  private async _outcomeCancelHandler() {
    this.outcome('cancel');
  }

  render() {
    return html`
      <ns-job-layout>
        <p slot="intro">Welcome back! We're thrilled that you've decided to continue your journey with us.</p>
        <div class="job-panel">
          <p>
            Your ${this.__subscription?.plan} subscription fee is
            ${renderMoney(this.__subscription?.billing.amount, this.__subscription?.billing.currency)}, the renewal date
            is ${renderDate(this.__subscription?.renewalDate, 'longDate')}.
          </p>
          <p>Thank you for choosing to stay with us. We're excited to have you back on board!</p>
        </div>
      </ns-job-layout>
    `;
  }

  renderActions() {
    return html`
      <wa-button variant="brand" type="button" @click=${() => this._outcomeCompleteHandler()}>Complete</wa-button>
    `;
  }
}
